import React, { memo, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Button, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { fadeUp } from 'utils/motion';
import useNotification from 'hooks/useNotification';
import FormTextField from 'components/ui/form/FormTextField';
import FormPasswordField from 'components/ui/form/FormPasswordField';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

const validationSchema = yup.object({
  email: yup
    .string()
    .email('client.addEdit.validation.physical.emailValid')
    .required('common.validation.required'),
  password: yup.string().required('login.validation.passwordRequired'),
  // nicknameId ixtiyoriy — berilmasa SMS qisqa raqamdan yuboriladi
  nickname_id: yup.string().nullable().optional(),
});

const TextupSettings = () => {
  const axiosPrivate = useAxiosPrivate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const sendNotification = useNotification();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      nickname_id: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const response = await axiosPrivate.post(
          '/admin/sms/textup-settings',
          values
        );
        if (response && response.data && response.data.status) {
          sendNotification({
            msg: t('settings.sms.alerts.systemSuccess'),
            variant: 'success',
          });
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);

        sendNotification({
          msg: error?.response?.data?.message || error?.message,
          variant: 'error',
        });
      }
    },
  });

  // Mavjud sozlamalarni yuklash (email va nicknameId). Parol backend'dan kelmaydi.
  useEffect(() => {
    let cancelled = false;
    axiosPrivate
      .get('/admin/sms/textup-settings')
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data;
        if (data?.email) formik.setFieldValue('email', data.email);
        if (data?.nickname_id)
          formik.setFieldValue('nickname_id', data.nickname_id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='login-wrapper w-96 px-5 p-4 my-shadow-1 rounded-lg'>
      <div className='flex justify-center my-2'>
        <span className='text-xl font-semibold'>Textup</span>
      </div>
      <form onSubmit={formik.handleSubmit}>
        <FormTextField
          delay={0.1}
          label={t('common.fields.email')}
          fieldName='email'
          formik={formik}
        />

        <FormPasswordField
          delay={0.2}
          label={t('common.fields.password')}
          fieldName='password'
          formik={formik}
        />

        <FormTextField
          delay={0.3}
          label={t('settings.sms.textupNicknameId')}
          fieldName='nickname_id'
          formik={formik}
        />

        <div className='text-center mt-3'>
          <Button
            fullWidth
            color='primary'
            variant='contained'
            type='submit'
            component={motion.button}
            variants={fadeUp(30, 'tween', 0.6, 0.5)}
            initial='hidden'
            animate='show'
            disabled={loading}
          >
            {loading && (
              <CircularProgress size={15} color='inherit' className='mr-1' />
            )}
            {t('login.button')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default memo(TextupSettings);
